<template>
    <AddFileSection
        :id="AddFileFormSectionIds.FILE_ADDRESS"
        :hasDivider="false"
        sectionHeading="Subject property"
    >
        <DsAddressAutocompleteSearch
            v-model="addFileStore.address"
            :id="FileDetailErrorKeys.ADDRESS"
            :actionableHelperText="!!addressAction"
            class="col-span-12 mt-4 md:col-span-6"
            :disabled="!!addFileStore.tpsOrder || addFileStore.isFormDisabled"
            :error="fileDetailErrors[FileDetailErrorKeys.ADDRESS]"
            :helperText="addressAction?.message"
            :maxlength="255"
            :name="FileDetailErrorKeys.ADDRESS"
            placeholder="Search for a property"
            required
            @clickHelperText="fieldActionHandler.onActionClick(addressAction)"
            @input="clearServerValidationError(FileDetailErrorKeys.ADDRESS)"
        />
    </AddFileSection>

    <AddFileSection
        :id="AddFileFormSectionIds.FILE_DETAILS"
        class="pt-0"
        :hasDivider="false"
        sectionHeading="File details"
    >
        <div class="mt-3 grid grid-cols-12 gap-4">
            <DsTextInput
                v-model.trim="addFileStore.refNumber"
                :id="FileDetailErrorKeys.REF_NUMBER"
                class="col-span-full md:col-span-6"
                :disabled="!!addFileStore.tpsOrder || addFileStore.isFormDisabled"
                :error="fileDetailErrors[FileDetailErrorKeys.REF_NUMBER]"
                label="File reference #"
                :maxlength="255"
                :name="FileDetailErrorKeys.REF_NUMBER"
                required
                type="text"
                @focusout="refNumberToCheck = addFileStore.refNumber"
                @input="clearServerValidationError(FileDetailErrorKeys.REF_NUMBER)"
            />

            <div class="col-span-12 grid grid-cols-12 gap-4">
                <SelectCompanyBranch
                    v-model:branch="addFileStore.branch"
                    v-model:company="addFileStore.company"
                    :branchError="fileDetailErrors[FileDetailErrorKeys.BRANCH_ID]"
                    :branches="userStore.activeBranches"
                    :companies="userStore.activeCompanies"
                    :companyError="fileDetailErrors[FileDetailErrorKeys.COMPANY_ID]"
                    :disableBranch="!!addFileStore.tpsOrder?.branch_id || addFileStore.isFormDisabled"
                    :disableCompany="!!addFileStore.tpsOrder?.title_id || addFileStore.isFormDisabled"
                    :initialBranchId="addFileStore.tpsOrder?.branch_id ?? userStore.defaultWireDocBranch?.id"
                    :initialCompanyId="addFileStore.tpsOrder?.title_id ?? userStore.defaultWireDocCompany?.id"
                    @fieldInput="clearServerValidationError($event)"
                />
            </div>

            <div
                v-if="userStore.showCloseDate"
                class="col-span-12 md:col-span-6 xl:col-span-4"
            >
                <DsDatePicker
                    v-model="addFileStore.closeDate"
                    v-model:openPicker="isCloseDatePickerOpen"
                    :disabled="addFileStore.isFormDisabled"
                    :input="{
                        label: 'Close date',
                        required: false,
                        error: fileDetailErrors[FileDetailErrorKeys.CLOSE_DATE],
                        helperText: closeDateAction?.message,
                        actionableHelperText: !!closeDateAction,
                    }"
                    :maxValue="maxCloseDate"
                    :minValue="minCloseDate"
                    @clickHelperText="fieldActionHandler.onActionClick(closeDateAction)"
                    @update:modelValue="clearServerValidationError(FileDetailErrorKeys.CLOSE_DATE)"
                />
            </div>
            <DsMoneyInput
                v-if="userStore.showBalanceDue"
                v-model="addFileStore.balanceDue"
                class="col-span-12 md:col-span-6 xl:col-span-4"
                :disabled="addFileStore.isFormDisabled"
                :error="fileDetailErrors[FileDetailErrorKeys.BALANCE_DUE]"
                label="Balance due"
                :moneyConfig="{ prefix: '$', disableNegative: true }"
                :name="FileDetailErrorKeys.BALANCE_DUE"
                type="text"
                @input="clearServerValidationError(FileDetailErrorKeys.BALANCE_DUE)"
            />
            <DsTextarea
                v-if="userStore.showNotes"
                v-model="addFileStore.notes"
                :id="FileDetailErrorKeys.NOTES"
                class="col-span-12"
                :disabled="addFileStore.isFormDisabled"
                :error="fileDetailErrors[FileDetailErrorKeys.NOTES]"
                helperText="Notes added here are visible to both your internal team and all client contacts listed on the file."
                label="Add file note"
                :name="FileDetailErrorKeys.NOTES"
                @input="clearServerValidationError(FileDetailErrorKeys.NOTES)"
            />
        </div>
        <slot />
    </AddFileSection>
</template>

<script setup lang="ts">
import { FileDetailErrorKeys } from '@/company-portal/add-file/add-file-form-errors.types';
import { useAddFileStore } from '@/company-portal/add-file/add-file.store';
import { ActionForFileField, FileFieldActionScope, AddFileFormSectionIds } from '@/company-portal/add-file/add-file.types';
import { useAddFileError } from '@/company-portal/add-file/use-add-file-error';
import DsDatePicker from '@/components/design-system/form/date-picker/DsDatePicker.vue';
import DsAddressAutocompleteSearch from '@/components/design-system/form/input/DsAddressAutocomplete/DsAddressAutocompleteSearch.vue';
import DsMoneyInput from '@/components/design-system/form/input/DsMoneyInput.vue';
import DsTextarea from '@/components/design-system/form/input/text/DsTextarea.vue';
import DsTextInput from '@/components/design-system/form/input/text/DsTextInput.vue';
import AddFileSection from '@/components/Views/Company/AddFile/AddFileSection.vue';
import SelectCompanyBranch from '@/components/Views/Company/shared/SelectCompanyBranch.vue';
import { FIELD_ACTIONS_INJECTION_KEY } from '@/injection-keys';
import { useUniqueFileRefNumber } from '@/shared/composables/use-unique-file-ref';
import { createCalendarDate } from '@/shared/utils/calendar-date-utils';
import { useUserStore } from '@/stores/company/user.store';
import { getLocalTimeZone, today } from '@internationalized/date';
import { computed, inject, ref, watch, watchEffect } from 'vue';

const addFileStore = useAddFileStore();
const userStore = useUserStore();
const { serverValidationErrors, clearServerValidationError } = useAddFileError();
const fieldActionHandler = inject(FIELD_ACTIONS_INJECTION_KEY, { onActionClick() {} });

const addressAction = computed(() =>
    addFileStore.fieldActions.find(
        action => action.scope === FileFieldActionScope.FILE && action.field === ActionForFileField.ADDRESS,
    ),
);

const closeDateAction = computed(() =>
    addFileStore.fieldActions.find(
        action => action.scope === FileFieldActionScope.FILE && action.field === ActionForFileField.CLOSE_DATE,
    ),
);

watchEffect(() => {
    addFileStore.refNumber = addFileStore.tpsOrder?.ref_number ?? '';
    addFileStore.address = addFileStore.tpsOrder?.address ?? '';
    const closeDateStr = addFileStore.tpsOrder?.close_date;
    if (closeDateStr) {
        const [month, day, year] = closeDateStr.split('/').map(Number);
        addFileStore.closeDate = month && day && year ? createCalendarDate(year, month, day) : undefined;
    }
    addFileStore.notes = addFileStore.tpsOrder?.notes ?? '';
});

// ref number
const { refNumberToCheck, refNumberIsUnique } = useUniqueFileRefNumber();

watch(refNumberIsUnique, value => {
    console.log('[uniqueRefNumberCheck] AddFile refNumberIsUnique:', value, 'for', refNumberToCheck.value);
});

// close date
const isCloseDatePickerOpen = ref(false);

const minCloseDate = computed(() => today(getLocalTimeZone()).subtract({ years: 5 }));
const maxCloseDate = computed(() => today(getLocalTimeZone()).add({ days: 90 }));

// errors
const fileDetailErrors = computed(() => ({
    [FileDetailErrorKeys.REF_NUMBER]: serverValidationErrors.value?.[FileDetailErrorKeys.REF_NUMBER]?.join(' '),
    [FileDetailErrorKeys.ADDRESS]: serverValidationErrors.value?.[FileDetailErrorKeys.ADDRESS]?.join(' '),
    [FileDetailErrorKeys.COMPANY_ID]: serverValidationErrors.value?.[FileDetailErrorKeys.COMPANY_ID]?.join(' '),
    [FileDetailErrorKeys.BRANCH_ID]: serverValidationErrors.value?.[FileDetailErrorKeys.BRANCH_ID]?.join(' '),
    [FileDetailErrorKeys.CLOSE_DATE]: serverValidationErrors.value?.[FileDetailErrorKeys.CLOSE_DATE]?.join(' '),
    [FileDetailErrorKeys.BALANCE_DUE]: serverValidationErrors.value?.[FileDetailErrorKeys.BALANCE_DUE]?.join(' '),
    [FileDetailErrorKeys.NOTES]: serverValidationErrors.value?.[FileDetailErrorKeys.NOTES]?.join(' '),
}));
</script>
